import React, { useState, useRef } from 'react';
import {
  Upload, Download, FileText, Settings, Play, CheckCircle,
  XCircle, BarChart3, Zap, FolderOpen, Trash2, Plus,
  Merge, Scissors, RotateCw, Droplet, FileCheck, Receipt
} from 'lucide-react';
import {
  bulkMergePDFs, bulkSplitPDFs, bulkConvertPDFs, bulkCompressPDFs,
  bulkAddWatermark, bulkRotatePDFs, bulkDetectForms, bulkExtractInvoices,
  processQueue, getProcessingStats
} from '../utils/bulkProcessingUtils';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import ToolActionButtons from './shared/ToolActionButtons';

const BulkProcessing = () => {
  const [files, setFiles] = useState([]);
  const [selectedOperation, setSelectedOperation] = useState('merge');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [stats, setStats] = useState(null);
  const fileInputRef = useRef(null);

  const operations = [
    { id: 'merge', label: 'Merge PDFs', icon: Merge, color: 'from-blue-500 to-cyan-500' },
    { id: 'split', label: 'Split PDFs', icon: Scissors, color: 'from-purple-500 to-pink-500' },
    { id: 'convert', label: 'Convert PDFs', icon: FileText, color: 'from-green-500 to-emerald-500' },
    { id: 'compress', label: 'Compress PDFs', icon: Zap, color: 'from-yellow-500 to-orange-500' },
    { id: 'watermark', label: 'Add Watermark', icon: Droplet, color: 'from-indigo-500 to-purple-500' },
    { id: 'rotate', label: 'Rotate PDFs', icon: RotateCw, color: 'from-red-500 to-pink-500' },
    { id: 'detect-forms', label: 'Detect Forms', icon: FileCheck, color: 'from-teal-500 to-cyan-500' },
    { id: 'extract-invoices', label: 'Extract Invoices', icon: Receipt, color: 'from-green-500 to-teal-500' },
  ];

  const handleFileUpload = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const pdfFiles = selectedFiles.filter(f => f.type === 'application/pdf');

    if (pdfFiles.length > 0) {
      setFiles(prev => [...prev, ...pdfFiles]);
      toast.success(`Added ${pdfFiles.length} file(s)`);
    } else {
      toast.error('Please select PDF files');
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleProcess = async () => {
    if (files.length === 0) {
      toast.error('Please upload files first');
      return;
    }

    try {
      setIsProcessing(true);
      setProgress(0);
      let processedResults = [];

      switch (selectedOperation) {
        case 'merge':
          // Group files for merging (you can customize this)
          const fileGroups = [{ name: 'Merged PDFs', files }];
          processedResults = await bulkMergePDFs(fileGroups, setProgress);
          break;
        case 'split':
          processedResults = await bulkSplitPDFs(
            files,
            { mode: 'every-n-pages', n: 1 },
            setProgress
          );
          break;
        case 'convert':
          processedResults = await bulkConvertPDFs(
            files,
            'pdf-to-docx',
            setProgress
          );
          break;
        case 'compress':
          processedResults = await bulkCompressPDFs(
            files,
            'medium',
            setProgress
          );
          break;
        case 'watermark':
          const watermarkText = prompt('Enter watermark text:') || 'CONFIDENTIAL';
          processedResults = await bulkAddWatermark(
            files,
            watermarkText,
            {},
            setProgress
          );
          break;
        case 'rotate':
          const rotation = parseInt(prompt('Enter rotation angle (90, 180, 270):') || '90');
          processedResults = await bulkRotatePDFs(
            files,
            rotation,
            setProgress
          );
          break;
        case 'detect-forms':
          processedResults = await bulkDetectForms(files, setProgress);
          break;
        case 'extract-invoices':
          processedResults = await bulkExtractInvoices(files, setProgress);
          break;
        default:
          throw new Error('Unknown operation');
      }

      setResults(processedResults);
      const processingStats = getProcessingStats(processedResults);
      setStats(processingStats);

      toast.success(`Processed ${processingStats.successful} file(s) successfully!`);
    } catch (error) {
      toast.error(`Processing failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadResult = (result) => {
    if (result.blob) {
      saveAs(result.blob, result.filename);
    } else if (result.files) {
      result.files.forEach(file => {
        saveAs(file.blob, file.filename);
      });
    }
  };

  const downloadAll = () => {
    if (!results) return;

    results.forEach(result => {
      if (result.success) {
        downloadResult(result);
      }
    });

    toast.success('Downloading all files...');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-indigo-900 to-gray-900 text-white p-6">
      {/* Action Buttons */}
      <ToolActionButtons
        onAdd={() => {
          fileInputRef.current?.click();
        }}
        notificationCount={files.length}
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="tool-header">
          <div className="tool-icon-wrapper">
            <FolderOpen size={40} color="var(--color-text-primary)" />
          </div>
          <div className="tool-header-content">
            <h1 className="tool-title">Bulk PDF Processing</h1>
            <p className="tool-description">
              Process multiple PDFs simultaneously for freelancers and agencies
            </p>
          </div>
        </div>

        {/* Operation Selection */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4">Select Operation</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {operations.map(op => {
              const Icon = op.icon;
              return (
                <button
                  key={op.id}
                  onClick={() => setSelectedOperation(op.id)}
                  className={`p-4 rounded-xl border-2 transition-all ${selectedOperation === op.id
                      ? `border-white/50 bg-gradient-to-r ${op.color} bg-opacity-20`
                      : 'border-white/10 bg-white/5 hover:bg-white/10'
                    }`}
                >
                  <Icon size={32} className="mx-auto mb-2" />
                  <div className="text-sm font-semibold">{op.label}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* File Upload */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Files ({files.length})</h2>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                multiple
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="btn-primary flex items-center gap-2"
              >
                <Plus size={18} />
                Add Files
              </button>
              {files.length > 0 && (
                <button
                  onClick={() => setFiles([])}
                  className="btn-outline flex items-center gap-2"
                >
                  <Trash2 size={18} />
                  Clear All
                </button>
              )}
            </div>
          </div>

          {files.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {files.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-white/5 rounded-lg p-3"
                >
                  <div className="flex items-center gap-3">
                    <FileText size={20} className="text-gray-400" />
                    <div>
                      <div className="font-medium">{file.name}</div>
                      <div className="text-sm text-gray-400">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <XCircle size={20} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <FolderOpen size={48} className="mx-auto mb-2 opacity-50" />
              <p>No files uploaded. Click "Add Files" to get started.</p>
            </div>
          )}
        </div>

        {/* Process Button */}
        {files.length > 0 && (
          <div className="mb-6">
            <button
              onClick={handleProcess}
              className="w-full btn-primary text-lg py-4 flex items-center justify-center gap-3"
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Play size={24} />
                  Process {files.length} File(s)
                </>
              )}
            </button>
          </div>
        )}

        {/* Progress Bar */}
        {isProcessing && (
          <div className="mb-6">
            <div className="bg-white/5 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-indigo-500 to-purple-500 h-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-gray-400 mt-2 text-center">
              Processing... {Math.round(progress)}%
            </p>
          </div>
        )}

        {/* Statistics */}
        {stats && (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-6">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <BarChart3 size={24} />
              Processing Statistics
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/5 rounded-lg p-4">
                <div className="text-3xl font-black text-indigo-400">{stats.total}</div>
                <div className="text-gray-400 text-sm">Total Files</div>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <div className="text-3xl font-black text-green-400">{stats.successful}</div>
                <div className="text-gray-400 text-sm">Successful</div>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <div className="text-3xl font-black text-red-400">{stats.failed}</div>
                <div className="text-gray-400 text-sm">Failed</div>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <div className="text-3xl font-black text-yellow-400">{stats.successRate}%</div>
                <div className="text-gray-400 text-sm">Success Rate</div>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Results</h2>
              {stats && stats.successful > 0 && (
                <button
                  onClick={downloadAll}
                  className="btn-primary flex items-center gap-2"
                >
                  <Download size={18} />
                  Download All
                </button>
              )}
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {results.map((result, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${result.success
                      ? 'bg-green-500/10 border-green-500/50'
                      : 'bg-red-500/10 border-red-500/50'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {result.success ? (
                        <CheckCircle className="text-green-400" size={24} />
                      ) : (
                        <XCircle className="text-red-400" size={24} />
                      )}
                      <div>
                        <div className="font-semibold">
                          {result.filename || result.groupName || `File ${index + 1}`}
                        </div>
                        {result.error && (
                          <div className="text-sm text-red-400">{result.error}</div>
                        )}
                        {result.compressionRatio && (
                          <div className="text-sm text-gray-400">
                            Compressed: {result.compressionRatio}
                          </div>
                        )}
                      </div>
                    </div>
                    {result.success && (result.blob || result.files) && (
                      <button
                        onClick={() => downloadResult(result)}
                        className="btn-outline flex items-center gap-2"
                      >
                        <Download size={18} />
                        Download
                      </button>
                    )}
                  </div>
                  {result.files && result.files.length > 0 && (
                    <div className="mt-3 pl-8 text-sm text-gray-400">
                      Generated {result.files.length} file(s)
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkProcessing;


